// ViewProductDetails.tsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import WhiteTitledCard from "../../../components/cards/WhiteTitledCard";
import { useAppSelector, useAppDispatch } from "../../../redux/app/hooks";
import { RootState } from "../../../redux/app/store";
import {
  approveProductAsync,
  fetchProductsDetailsAsync,
} from "../../../redux/features/ProductSlice";
import { Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import EditProductModal from "./UpdateProductDetails";

const StatusTag = ({ status }: { status?: string }) => {
  const statusColors: Record<string, string> = {
    PENDING: "bg-yellow-100 text-yellow-700 border-yellow-200",
    APPROVED: "bg-green-100 text-green-700 border-green-200",
    REJECTED: "bg-red-100 text-red-700 border-red-200",
    ACTIVE: "bg-blue-100 text-blue-700 border-blue-200",
    INACTIVE: "bg-gray-100 text-gray-700 border-gray-200",
  };

  const classes =
    statusColors[status ?? ""] ?? "bg-gray-100 text-gray-700 border-gray-200";

  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold border ${classes}`}
    >
      {status
        ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
        : "—"}
    </span>
  );
};

const Detail = ({ label, value }: { label?: string; value?: any }) => (
  <div>
    <span className="text-gray-500 text-sm">{label}:</span>
    <p className="font-medium text-gray-800 mt-1">{value ?? "—"}</p>
  </div>
);

const ViewProductDetails: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [rejectionReasonInput, setRejectionReasonInput] = useState("");

  // pulls from product state (adjust names if your slice uses different keys)

  const [openModal, setOpenModal] = useState<{
    type: "approve" | "reject" | null;
  }>({ type: null });
  const [photoIndex, setPhotoIndex] = useState(0);
  const [openEditModal, setOpenEditModal] = useState(false);
  const { loading, productDetails } = useAppSelector(
    (state: RootState) => state.products
  );

  // find product in lists OR use selectedProduct from state (if you set it before navigating)
  const { id: prodId } = useParams();

  useEffect(() => {
    dispatch(fetchProductsDetailsAsync(prodId));
  }, [prodId, dispatch]);

  if (loading && !productDetails) {
    return (
      <div className="p-6 flex justify-center items-center min-h-screen">
        <Loader2 className="w-10 h-10 animate-spin text-gray-500" />
      </div>
    );
  }

  if (!productDetails) {
    return (
      <div className="p-6">
        <button
          className="text-sm text-gray-600 mb-4"
          onClick={() => navigate(-1)}
        >
          &larr; Back
        </button>
        <WhiteTitledCard>
          <div className="p-6">
            <p className="text-gray-700">Product not found</p>
          </div>
        </WhiteTitledCard>
      </div>
    );
  }

  const {
    name,
    description,
    photos = [],
    price,
    quantity,
    colors,
    sizes,
    tags,
    soldQuantity,
    approvalStatus,
    approvedBy,
    approvedAt,
    rejectionReason,
    status,
    currencyId,
    categoryIds,
    vendorId,
    createdAt,
    updatedAt,
    id: productId,
  } = productDetails;

  const handleConfirmApprove = async () => {
    const newStatus = openModal.type === "approve" ? "APPROVED" : "REJECTED";

    // ✅ If rejecting, require reason
    if (newStatus === "REJECTED" && !rejectionReasonInput.trim()) {
      toast.error("Rejection reason is required");
      return;
    }

    await dispatch(
      approveProductAsync({
        productId: productId,
        approvalStatus: newStatus,
        rejectionReason:
          newStatus === "REJECTED" ? rejectionReasonInput.trim() : undefined,
      })
    ).unwrap();

    toast.success(
      `Product ${newStatus.toLowerCase()} successfully${
        newStatus === "REJECTED" ? ` with reason: ${rejectionReasonInput}` : ""
      }`
    );
    setOpenModal({ type: null });
    setRejectionReasonInput(""); // reset
  };

  return (
    <div className="p-6 sm:p-8 bg-gray-50 min-h-screen">
      <button
        className="text-gray-600 mb-4 inline-flex items-center gap-2"
        onClick={() => navigate(-1)}
      >
        &larr; Back
      </button>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: images */}
        <div className="lg:col-span-1 gap-6 flex flex-col">
          <WhiteTitledCard>
            <div>
              <div className="w-full h-64 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center">
                {photos && photos.length > 0 ? (
                  // main image
                  <img
                    src={photos[photoIndex]}
                    alt={`${name}  ${photoIndex}`}
                    className="object-cover w-full h-full"
                  />
                ) : (
                  <div className="text-gray-400">No image</div>
                )}
              </div>

              {photos && photos.length > 1 && (
                <div className="my-3 grid grid-cols-4 gap-2">
                  {photos.map((p: string, i: number) => (
                    <img
                      key={p + i}
                      src={p}
                      alt={`${name} thumb ${i}`}
                      onClick={() => setPhotoIndex(i)}
                      className={`h-16 w-full object-cover rounded cursor-pointer border ${
                        i === photoIndex
                          ? "ring-2 ring-indigo-400"
                          : "border-gray-200"
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </WhiteTitledCard>

          {/* Description */}
          <WhiteTitledCard>
            <h3 className="text-lg font-semibold mb-2">Description</h3>
            <div className="text-sm text-gray-700 whitespace-pre-wrap">
              {description ?? "—"}
            </div>
          </WhiteTitledCard>
        </div>

        {/* Right column: details */}
        <div className="lg:col-span-2 space-y-6">
          <WhiteTitledCard>
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">{name}</h1>
                <div className="gap-3 mt-2">
                  <p className="text-sm font-semibold text-gray-700">
                    Category:
                  </p>
                  <div className="flex gap-3">
                    <span className=" py-1 text-sm   text-gray-600 ">
                      {categoryIds?.title ?? "Uncategorized"}
                    </span>

                    {/* <button
                      onClick={() => setOpenCategoryModal(true)}
                      className=" py-1 text-xs font-medium underline text-primary cursor-pointer"
                    >
                      Edit Category
                    </button> */}
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-end gap-2">
                <StatusTag status={approvalStatus ?? status} />
                {/* <span className="text-xs text-gray-500">{`ID: ${
                  productId ?? product._id
                }`}</span> */}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <Detail
                label="Price"
                value={`${currencyId?.symbol ?? ""} ${price ?? "—"}`}
              />
              <Detail label="Quantity" value={quantity ?? "—"} />
              <Detail label="Sold" value={soldQuantity ?? 0} />
            </div>
          </WhiteTitledCard>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <WhiteTitledCard>
              <h3 className="text-lg font-semibold mb-3">Attributes</h3>
              <div className="space-y-3">
                <Detail
                  label="Colors"
                  value={
                    Array.isArray(colors)
                      ? colors.length
                        ? colors.join(", ")
                        : "—"
                      : colors ?? "—"
                  }
                />

                <div>
                  <span className="text-gray-500 text-sm">Size Details:</span>
                  {sizes?.map((size: any) => (
                    <p className="font-medium text-gray-800 mt-1">
                      {size?.sizeVariant
                        ? `${size?.sizeVariant.label} — ${Object.entries(
                            size?.sizeVariant.measurements
                          )
                            .map(([key, value]) => `${key}: ${value}`)
                            .join(", ")} — Stock: ${size?.stock}`
                        : "—"}
                    </p>
                  ))}
                </div>

                <Detail
                  label="Tags"
                  value={
                    Array.isArray(tags)
                      ? tags.length
                        ? tags.join(", ")
                        : "—"
                      : tags ?? "—"
                  }
                />
              </div>
            </WhiteTitledCard>

            <WhiteTitledCard>
              <h3 className="text-lg font-semibold mb-3">Vendor</h3>
              <div className="space-y-3">
                <Detail
                  label="Vendor Name"
                  value={
                    vendorId?.fullName ??
                    `${vendorId?.firstName ?? ""} ${vendorId?.lastName ?? ""}`
                  }
                />
                <Detail label="Vendor Email" value={vendorId?.email ?? "—"} />
                <Detail
                  label="Vendor Phone"
                  value={vendorId?.phoneNumber ?? "—"}
                />
              </div>
            </WhiteTitledCard>
          </div>

          <WhiteTitledCard>
            <h3 className="text-lg font-semibold mb-3">Approval & Status</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Detail label="Approval Status" value={approvalStatus ?? "—"} />
              <Detail
                label="Approved By"
                value={approvedBy?.fullName ?? approvedBy ?? "—"}
              />
              <Detail
                label="Approved At"
                value={
                  approvedAt
                    ? moment(approvedAt).format("Do MMM YYYY, h:mm A")
                    : "—"
                }
              />
              <Detail label="Rejection Reason" value={rejectionReason ?? "—"} />
              <Detail label="Status" value={status ?? "—"} />
              {/* <Detail label="Category ID" value={categoryIds?.id ?? "—"} /> */}
            </div>
          </WhiteTitledCard>

          <WhiteTitledCard>
            {/* <h3 className="text-lg font-semibold mb-3"></h3> */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Detail
                label="Created At"
                value={
                  createdAt
                    ? moment(createdAt).format("Do MMM YYYY, h:mm A")
                    : "—"
                }
              />
              <Detail
                label="Updated At"
                value={
                  updatedAt
                    ? moment(updatedAt).format("Do MMM YYYY, h:mm A")
                    : "—"
                }
              />
              <Detail
                label="Currency"
                value={currencyId?.name ?? currencyId?.symbol ?? "—"}
              />
            </div>
          </WhiteTitledCard>

          <div className="flex justify-end gap-4">
            <button
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
              onClick={() => setOpenEditModal(true)}
            >
              Edit Product
            </button>

            {(approvalStatus === "PENDING" ||
              approvalStatus === "APPROVED") && (
              <button
                className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
                onClick={() => setOpenModal({ type: "reject" })}
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Reject"
                )}
              </button>
            )}
            {(approvalStatus === "PENDING" ||
              approvalStatus === "REJECTED") && (
              <button
                className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                onClick={() => setOpenModal({ type: "approve" })}
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Approve"
                )}
              </button>
            )}
          </div>
        </div>
      </div>
      {/* Modal */}
      {openModal.type && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6">
            <h2 className="text-lg font-semibold mb-3 capitalize">
              {openModal.type} Product
            </h2>
            <p className="text-sm text-gray-700">
              Are you sure you want to{" "}
              <span className="font-semibold">{openModal.type}</span>{" "}
              <span className="font-semibold">{name}</span>?
            </p>

            {openModal.type === "reject" && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rejection Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="w-full border rounded-md p-2 text-sm focus:ring focus:ring-red-300"
                  rows={3}
                  value={rejectionReasonInput}
                  onChange={(e) => setRejectionReasonInput(e.target.value)}
                  disabled={loading}
                  placeholder="Enter reason for rejection"
                />
              </div>
            )}

            <div className="flex gap-3 justify-end mt-6">
              <button
                className="px-4 py-2 rounded border border-gray-300"
                onClick={() => setOpenModal({ type: null })}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2 rounded text-white flex items-center gap-2 ${
                  openModal.type === "approve"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-red-500 hover:bg-red-600"
                } disabled:opacity-50`}
                onClick={handleConfirmApprove}
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  `Confirm ${openModal.type}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <EditProductModal
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
        }}
        product={productDetails}
        onSave={() => {
          dispatch(fetchProductsDetailsAsync(prodId));
        }}
      />
    </div>
  );
};

export default ViewProductDetails;
